import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Gift, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import ShareButtons from "@/components/ShareButtons";

export default function ReferralPage() {
  const { user, loading } = useAuth();
  const { lang } = useLang();
  const t = useDict(lang);
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!loading && !user) navigate("/auth");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.myReferral().then(setData).catch(() => {});
  }, [user]);

  if (!user || !data) return null;

  const baseUrl = `${window.location.origin}?ref=${data.code}`;
  const shareText = t.referral.shareText.replace("{code}", data.code);

  return (
    <div data-testid="referral-page" className="max-w-[1100px] mx-auto px-6 lg:px-12 pt-16 pb-24">
      <Link to="/profile" className="eyebrow inline-block mb-6">{t.referral.back}</Link>
      <p className="eyebrow mb-3 flex items-center gap-3">
        <Gift size={14} color="var(--red)" strokeWidth={1.5} />
        {t.referral.eyebrow}
      </p>
      <h1 className="font-serif-display text-5xl sm:text-6xl leading-[1.02] mb-6">
        {t.referral.title} <em className="not-italic text-[color:var(--red)]">{t.referral.titleAccent}</em>
      </h1>
      <p className="text-base text-[color:var(--ink-2)] max-w-2xl leading-relaxed mb-12">
        {t.referral.intro.replace("{rate}", Math.round(data.discount_rate * 100))}
      </p>

      <div className="grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-7">
          <div className="border border-[color:rgba(0,16,48,0.12)] p-6 lg:p-10 bg-white">
            <p className="eyebrow mb-3">{t.referral.codeLabel}</p>
            <p data-testid="referral-code" className="font-serif-display text-4xl sm:text-5xl tracking-wide mb-6 select-all">
              {data.code}
            </p>
            <p className="eyebrow mb-3">{t.referral.linkLabel}</p>
            <p data-testid="referral-link" className="text-sm break-all bg-[var(--bg-2)] px-3 py-2 mb-6 font-mono">
              {baseUrl}
            </p>
            <ShareButtons url={baseUrl} text={shareText} />
          </div>

          <div className="mt-8 p-6 border-l-2 border-[color:var(--mango)] bg-[var(--bg-2)]">
            <p className="eyebrow mb-2 flex items-center gap-2">
              <Sparkles size={14} strokeWidth={1.5} /> {t.referral.howTitle}
            </p>
            <ol className="space-y-2 text-sm text-[color:var(--ink-2)] mt-3 list-decimal pl-5">
              <li>{t.referral.steps[0]}</li>
              <li>{t.referral.steps[1].replace("{rate}", Math.round(data.discount_rate * 100))}</li>
              <li>{t.referral.steps[2]}</li>
            </ol>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="grid grid-cols-3 gap-3 mb-8">
            <Stat label={t.referral.stats.signups} value={data.stats.signups} />
            <Stat label={t.referral.stats.purchased} value={data.stats.purchased} />
            <Stat label={t.referral.stats.rewards} value={data.stats.rewards_sent} />
          </div>
          <div className="border border-[color:rgba(0,16,48,0.12)] p-6 bg-white">
            <p className="eyebrow mb-2">{t.referral.rewardTitle}</p>
            <h3 className="font-serif-display text-2xl leading-tight mb-3">
              {data.reward_ebook[lang === "en" ? "title_en" : "title_fr"]}
            </h3>
            <p className="text-sm text-[color:var(--ink-2)] leading-relaxed">{t.referral.rewardSub}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-[color:rgba(0,16,48,0.12)] p-4 text-center bg-white">
      <p className="font-serif-display text-4xl leading-none">{value}</p>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--ink-3)] mt-2 leading-tight">{label}</p>
    </div>
  );
}
